import { useCallback, useMemo } from 'react';

import { Offer as OfferType, Product } from 'queries';
import { Theme } from '@app/theme';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Chip } from '@app/core';
import { toEuros } from 'i18n';
import { useSupportOfferIllustration } from './utils/useSupportOfferIllustration';
import { filterAlreadySelected } from 'lib/planAndOffer';
import { getSimpleOfferPrice } from 'lib/priceComputation';

interface Props {
  offer: OfferType;
  alreadySelectedProducts?: Product[];
  className?: string;
  onClick?: (offer: OfferType) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    padding: theme.marginBase * 2,

    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius,

    '&:hover': {
      backgroundColor: theme.colors.grayBackground,
    },
  },
  illustration: {
    width: theme.height.large,
    height: theme.height.large,
  },
  textContent: {
    display: 'flex',
    gap: theme.marginBase,
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    textAlign: 'left',
  },
  name: {
    ...theme.fonts.h5,
  },
  description: {
    ...theme.fonts.caption,
  },
}));

export const CompactSupportOfferBlock = ({
  offer,
  className,
  alreadySelectedProducts = [],
  onClick,
}: Props) => {
  const classes = useStyles();

  const { amount: total } = useMemo(
    () =>
      getSimpleOfferPrice(
        filterAlreadySelected(offer.products, alreadySelectedProducts),
      ),
    [offer.products, alreadySelectedProducts],
  );

  const illustration = useSupportOfferIllustration(offer);
  const onButtonClick = useCallback(
    () => onClick && onClick(offer),
    [offer, onClick],
  );

  return (
    <button
      className={classNames(classes.container, className)}
      onClick={onButtonClick}
      type="button"
    >
      {illustration && (
        <img
          src={illustration}
          alt={offer.name}
          className={classes.illustration}
        />
      )}
      <div className={classes.textContent}>
        <div className={classes.name}>{offer.name}</div>
        {offer.description && (
          <div className={classes.description}>{offer.description}</div>
        )}
      </div>
      <Chip color="accent">{toEuros(total)}</Chip>
    </button>
  );
};
